// Server-Sent Events over a POST `fetch` — the only way to stream while keeping the Bearer
// token in a header (`EventSource` cannot send one). This file owns the wire format only;
// each consumer maps frames onto its own handlers.

/** One complete SSE frame: the `event:` name (`'message'` when unnamed) plus its parsed `data:` JSON. */
export interface SseFrame {
  event: string;
  payload: Record<string, unknown>;
}

/**
 * Parse one decoded SSE frame (`event:` + one or more `data:` lines). Returns null for
 * keep-alive comments, dataless frames and unparseable payloads — a malformed frame is
 * skipped rather than aborting the whole stream.
 */
export function parseSseFrame(raw: string): SseFrame | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue; // blank or keep-alive comment
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  if (dataLines.length === 0) return null;

  try {
    return { event, payload: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}

/**
 * Read a `text/event-stream` body to completion, handing each parsed frame to `onFrame`.
 * A throw from `onFrame` (the idiom for a server-sent `error` event) aborts the read and
 * propagates to the caller; the reader lock is released either way.
 */
export async function readSseStream(
  body: NonNullable<Response['body']>,
  onFrame: (frame: SseFrame) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Frames are separated by a blank line (\n\n).
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = parseSseFrame(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        if (frame) onFrame(frame);
      }
    }
    // Flush any trailing frame without a terminating blank line.
    if (buffer.trim()) {
      const frame = parseSseFrame(buffer);
      if (frame) onFrame(frame);
    }
  } finally {
    reader.releaseLock();
  }
}
